import { produce } from "immer";
import React, { useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Options as RemarkRehypeOptions } from "remark-rehype";
import {
  Citation,
  Reference,
} from "../../adapters/insights-agent/state/AppState.ts";
import { CitationMarkerInjector } from "./lib/CitationMarkerInjector.ts";
import { remarkCitationInjector } from "./lib/remarkCitationPlugin.ts";
import rehypeCitationPlugin from "./lib/rehypeCitationPlugin.ts";
import { removeTables } from "./removeTables.ts";
import styles from "../../ui-library/MarkdownContent.module.css";

const markerInjector = new CitationMarkerInjector();

export type CitationComponent = React.FC<{
  referenceIds: string[];
  data: Reference[];
}>;

const remarkRehypeOptions: Readonly<RemarkRehypeOptions> = {
  // This is a workaround for the issue with react-rehype types
  passThrough: ["citation" as unknown as "blockquote"],
};
export const MarkdownWithCitations: React.FC<{
  markdown: string;
  citations: Citation[];
  references: Reference[];
  CitationComponent: CitationComponent;
  splitCitations?: boolean;
}> = (props) => {
  const markedText = useMemo(
    () =>
      markerInjector.injectMarkers(props.markdown, props.citations, {
        splitCitations: props.splitCitations || false,
      }),
    [props.citations, props.markdown, props.splitCitations],
  );

  return (
    <div className={styles.MarkdownContent}>
      <Markdown
        remarkPlugins={[
          remarkGfm,
          [
            remarkCitationInjector,
            produce(props.references, (draft) => {
              draft.forEach((reference) => {
                reference.excerpt = removeTables(reference.excerpt);
              });
            }),
          ],
        ]}
        rehypePlugins={[
          [
            rehypeCitationPlugin,
            { CitationComponent: props.CitationComponent },
          ],
        ]}
        remarkRehypeOptions={remarkRehypeOptions}
        children={markedText}
      />
    </div>
  );
};
